import React, { useEffect, useState } from 'react';
import {
  Table, Input, Tag, Button, Modal, Select, message, Space, Card,
  Drawer, Descriptions, Divider, Badge, Alert, Spin, Tooltip, Form
} from 'antd';
import {
  MailOutlined, PhoneOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined, LockOutlined
} from '@ant-design/icons';
import API from '../api';

export default function Users() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // A2: User 360 view state
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [userDetails, setUserDetails] = useState<any | null>(null);

  // A4: OTP / verification status
  const [otpStatus, setOtpStatus] = useState<any | null>(null);

  // A22: Impersonation read-only view
  const [impersonateVisible, setImpersonateVisible] = useState(false);
  const [impersonateBookings, setImpersonateBookings] = useState<any[]>([]);
  const [loadingImpersonate, setLoadingImpersonate] = useState(false);

  // A5: Ban/Suspend/Reinstate modals
  const [banVisible, setBanVisible] = useState(false);
  const [suspendVisible, setSuspendVisible] = useState(false);
  const [reinstateVisible, setReinstateVisible] = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    const req = search ? API.get('/users/search', { params: { query: search } }) : API.get('/users');
    req.then(r => setUsers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleBan = async (values: any) => {
    if (!selectedUser) return;
    try {
      await API.post(`/users/${selectedUser.id}/ban`, values);
      message.success('User permanently banned');
      setBanVisible(false);
      form.resetFields();
      load();
      if (detailsVisible) fetchUserDetails(selectedUser.id);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to ban user');
    }
  };

  const handleSuspend = async (values: any) => {
    if (!selectedUser) return;
    try {
      await API.post(`/users/${selectedUser.id}/suspend`, values);
      message.success('User suspended');
      setSuspendVisible(false);
      form.resetFields();
      load();
      if (detailsVisible) fetchUserDetails(selectedUser.id);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to suspend user');
    }
  };

  const handleReinstate = async (values: any) => {
    if (!selectedUser) return;
    try {
      await API.post(`/users/${selectedUser.id}/reinstate`, values);
      message.success('User reinstated');
      setReinstateVisible(false);
      form.resetFields();
      load();
      if (detailsVisible) fetchUserDetails(selectedUser.id);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to reinstate user');
    }
  };

  const fetchUserDetails = async (id: number) => {
    setLoadingDetails(true);
    try {
      const { data } = await API.get(`/users/${id}/360`);
      setUserDetails(data);
    } catch {
      setUserDetails({ walletBalance: 0, activeJobs: 0, completedJobs: 0, documents: [] });
    } finally {
      setLoadingDetails(false);
    }
  };

  // A4: Fetch OTP / verification status
  const fetchOtpStatus = async (id: number) => {
    try {
      const { data } = await API.get(`/users/${id}/otp-status`);
      setOtpStatus(data);
    } catch {
      setOtpStatus(null);
    }
  };

  // A4: Force-send password reset email
  const handlePasswordReset = (user: any) => {
    Modal.confirm({
      title: 'Send Password Reset Email',
      content: `This will send a password reset link to ${user.email}. The user will receive an email with a 6-digit reset code.`,
      okText: 'Send Reset Email',
      onOk: () =>
        API.post(`/users/${user.id}/force-password-reset`)
          .then(() => message.success(`Reset email sent to ${user.email}`))
          .catch(() => message.error('Failed to send reset email')),
    });
  };

  const openUserDetails = (user: any) => {
    setSelectedUser(user);
    setDetailsVisible(true);
    setOtpStatus(null);
    fetchUserDetails(user.id);
    fetchOtpStatus(user.id);
  };

  // A22: Open impersonation read-only view
  const openImpersonation = async (user: any) => {
    setSelectedUser(user);
    setImpersonateVisible(true);
    setLoadingImpersonate(true);
    setImpersonateBookings([]);
    try {
      const [details, bookings] = await Promise.all([
        API.get(`/users/${user.id}/360`).catch(() => ({ data: {} })),
        API.get(`/users/${user.id}/bookings`).catch(() => ({ data: [] })),
      ]);
      setUserDetails(details.data);
      setImpersonateBookings(bookings.data || []);
    } catch {
      setImpersonateBookings([]);
    } finally {
      setLoadingImpersonate(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Name', dataIndex: 'fullName' },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Phone', dataIndex: 'phoneNumber', render: (v: string) => v || '—' },
    {
      title: 'Role', dataIndex: 'role',
      render: (v: string) => <Tag color={v === 'PROVIDER' ? 'purple' : v === 'ADMIN' ? 'red' : 'blue'}>{v}</Tag>
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (v: string) => {
        const colors: Record<string, string> = { ACTIVE: 'green', SUSPENDED: 'orange', BANNED: 'red', DELETED: 'default' };
        return <Tag color={colors[v] || 'blue'}>{v}</Tag>
      }
    },
    { title: 'Joined', dataIndex: 'createdAt', render: (v: string) => v ? new Date(v).toLocaleDateString() : '—' },
    {
      title: 'Actions', render: (_: any, r: any) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openUserDetails(r)}>360°</Button>
          <Tooltip title="Read-only view of user's app state">
            <Button size="small" icon={<LockOutlined />} onClick={() => openImpersonation(r)}>Impersonate</Button>
          </Tooltip>
          {r.status === 'ACTIVE' && (
            <Space>
              <Button size="small" danger onClick={() => { setSelectedUser(r); setSuspendVisible(true); }}>Suspend</Button>
              <Button size="small" danger type="primary" onClick={() => { setSelectedUser(r); setBanVisible(true); }}>Ban</Button>
            </Space>
          )}
          {(r.status === 'SUSPENDED' || r.status === 'BANNED') && (
            <Button size="small" type="primary" onClick={() => { setSelectedUser(r); setReinstateVisible(true); }}>Reinstate</Button>
          )}
        </Space>
      ),
    },
  ];

  const bookingColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    {
      title: 'Status', dataIndex: 'status',
      render: (v: string) => {
        const colors: Record<string, string> = { COMPLETED: 'green', CANCELLED: 'red', IN_PROGRESS: 'blue', ACCEPTED: 'cyan', PENDING: 'orange' };
        return <Tag color={colors[v] || 'default'}>{v}</Tag>;
      }
    },
    { title: 'Amount', dataIndex: 'finalPrice', render: (v: number) => v ? `R${v.toFixed(2)}` : '—' },
    { title: 'Date', dataIndex: 'createdAt', render: (v: string) => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <h2>User Management</h2>
        <Input.Search
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onSearch={load}
          enterButton
          style={{ width: 400 }}
          allowClear
        />
      </Space>

      <Table
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 15 }}
      />

      {/* A2: User 360° View drawer */}
      <Drawer
        title={`User 360° View — ${selectedUser?.fullName}`}
        placement="right"
        width={600}
        onClose={() => setDetailsVisible(false)}
        open={detailsVisible}
      >
        {selectedUser && (
          <div>
            <Descriptions title="Profile" bordered column={1} size="small">
              <Descriptions.Item label="ID">{selectedUser.id}</Descriptions.Item>
              <Descriptions.Item label="Name">{selectedUser.fullName}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedUser.phoneNumber || '—'}</Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color={selectedUser.role === 'PROVIDER' ? 'purple' : selectedUser.role === 'ADMIN' ? 'red' : 'blue'}>
                  {selectedUser.role}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedUser.status === 'ACTIVE' ? 'green' : selectedUser.status === 'SUSPENDED' ? 'orange' : 'red'}>
                  {selectedUser.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Joined">{new Date(selectedUser.createdAt).toLocaleString()}</Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* A4: OTP / Verification Status */}
            <h4>Verification Status</h4>
            {otpStatus ? (
              <Space wrap>
                <Tag icon={otpStatus.emailVerified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  color={otpStatus.emailVerified ? 'success' : 'error'}>
                  Email {otpStatus.emailVerified ? 'Verified' : 'Not Verified'}
                </Tag>
                <Tag icon={otpStatus.phoneVerified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  color={otpStatus.phoneVerified ? 'success' : 'error'}>
                  Phone {otpStatus.phoneVerified ? 'Verified' : 'Not Verified'}
                </Tag>
                {otpStatus.emailCodePending && (
                  <Tag color="warning" icon={<MailOutlined />}>Email OTP Pending</Tag>
                )}
                {otpStatus.phoneCodePending && (
                  <Tag color="warning" icon={<PhoneOutlined />}>Phone OTP Pending</Tag>
                )}
              </Space>
            ) : (
              <Tag color="default">Status unavailable</Tag>
            )}

            <Divider />

            {loadingDetails ? (
              <Spin />
            ) : userDetails && (
              <Descriptions title="Platform Data" bordered column={1} size="small">
                <Descriptions.Item label="Wallet Balance">R {userDetails.walletBalance?.toFixed(2) || '0.00'}</Descriptions.Item>
                <Descriptions.Item label="Active Jobs">{userDetails.activeJobs || 0}</Descriptions.Item>
                <Descriptions.Item label="Completed Jobs">{userDetails.completedJobs || 0}</Descriptions.Item>
                <Descriptions.Item label="Documents Uploaded">{userDetails.documents?.length || 0}</Descriptions.Item>
              </Descriptions>
            )}

            <Divider />

            <h4>Administrative Actions</h4>
            <Space wrap>
              {selectedUser.status === 'ACTIVE' ? (
                <>
                  <Button danger onClick={() => setSuspendVisible(true)}>Suspend Account</Button>
                  <Button danger type="primary" onClick={() => setBanVisible(true)}>Ban Account</Button>
                </>
              ) : (
                <Button type="primary" onClick={() => setReinstateVisible(true)}>Reinstate Account</Button>
              )}
              {/* A4: Wired password reset */}
              <Button icon={<MailOutlined />} onClick={() => handlePasswordReset(selectedUser)}>
                Send Password Reset
              </Button>
            </Space>
          </div>
        )}
      </Drawer>

      {/* Ban Modal */}
      <Modal
        title={`Permanently Ban ${selectedUser?.fullName}`}
        open={banVisible}
        onCancel={() => { setBanVisible(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Ban User"
        okType="danger"
      >
        <Alert
          message="Permanent Action"
          description="Banning will logout the user, cancel all active bookings, and permanently prevent them from using the platform."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical" onFinish={handleBan}>
          <Form.Item name="reason" label="Primary Reason" rules={[{ required: true }]}>
            <Select placeholder="Select a reason">
              <Select.Option value="FRAUD">Fraudulent Activity</Select.Option>
              <Select.Option value="MISCONDUCT">Serious Misconduct</Select.Option>
              <Select.Option value="OFF_PLATFORM_PAYMENT">Off-platform Payment</Select.Option>
              <Select.Option value="POPIA_VIOLATION">POPIA/Privacy Violation</Select.Option>
              <Select.Option value="ABUSE">Abuse or Harassment</Select.Option>
              <Select.Option value="FAKE_ACCOUNT">Fake or Misleading Account</Select.Option>
              <Select.Option value="CHARGEBACK_ABUSE">Chargeback Abuse</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Detailed Notes (Internal)" rules={[{ required: true, min: 10 }]}>
            <Input.TextArea rows={4} placeholder="Detailed explanation for audit log..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Suspend Modal */}
      <Modal
        title={`Suspend ${selectedUser?.fullName}`}
        open={suspendVisible}
        onCancel={() => { setSuspendVisible(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Suspend User"
      >
        <Form form={form} layout="vertical" onFinish={handleSuspend}>
          <Form.Item name="reason" label="Suspension Reason" rules={[{ required: true }]}>
            <Select placeholder="Select a reason">
              <Select.Option value="INVESTIGATION">Under Investigation</Select.Option>
              <Select.Option value="PAYMENT_ISSUE">Payment/Wallet Issue</Select.Option>
              <Select.Option value="COMPLAINT_RECEIVED">Customer Complaint</Select.Option>
              <Select.Option value="DOCUMENT_EXPIRED">Documents Expired</Select.Option>
              <Select.Option value="MANUAL_REVIEW">Manual Review Required</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="durationDays" label="Duration (Days)">
            <Select placeholder="Indefinite" allowClear>
              <Select.Option value={1}>1 Day</Select.Option>
              <Select.Option value={3}>3 Days</Select.Option>
              <Select.Option value={7}>7 Days</Select.Option>
              <Select.Option value={14}>14 Days</Select.Option>
              <Select.Option value={30}>30 Days</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Internal Notes" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Explain why the user is being suspended..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reinstate Modal */}
      <Modal
        title={`Reinstate ${selectedUser?.fullName}`}
        open={reinstateVisible}
        onCancel={() => { setReinstateVisible(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Reinstate User"
      >
        <p>Are you sure you want to reinstate this user account? They will regain full access immediately.</p>
        <Form form={form} layout="vertical" onFinish={handleReinstate}>
          <Form.Item name="notes" label="Reinstatement Notes" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Provide justification for reinstatement..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* A22: Impersonation read-only view */}
      <Modal
        title={null}
        open={impersonateVisible}
        onCancel={() => setImpersonateVisible(false)}
        footer={<Button onClick={() => setImpersonateVisible(false)}>Close</Button>}
        width={800}
      >
        <Alert
          message="Admin Read-Only View"
          description={`You are viewing the app state as ${selectedUser?.fullName}. No transactions or changes can be made from this view.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {loadingImpersonate ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
        ) : selectedUser && (
          <>
            <Card size="small" title="User Profile" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Name">{selectedUser.fullName}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedUser.phoneNumber || '—'}</Descriptions.Item>
                <Descriptions.Item label="Role">
                  <Tag color={selectedUser.role === 'PROVIDER' ? 'purple' : 'blue'}>{selectedUser.role}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Account Status">
                  <Tag color={selectedUser.status === 'ACTIVE' ? 'green' : selectedUser.status === 'SUSPENDED' ? 'orange' : 'red'}>
                    {selectedUser.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Member Since">{new Date(selectedUser.createdAt).toLocaleDateString()}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Wallet & Activity" style={{ marginBottom: 16 }}>
              <Descriptions column={3} size="small">
                <Descriptions.Item label="Wallet Balance">
                  <strong>R {userDetails?.walletBalance?.toFixed(2) || '0.00'}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Active Jobs">{userDetails?.activeJobs || 0}</Descriptions.Item>
                <Descriptions.Item label="Completed Jobs">{userDetails?.completedJobs || 0}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title={`Booking History (${impersonateBookings.length} records)`}>
              {impersonateBookings.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>No bookings found for this user.</p>
              ) : (
                <Table
                  dataSource={impersonateBookings.slice(0, 20)}
                  columns={bookingColumns}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  scroll={{ y: 280 }}
                />
              )}
            </Card>
          </>
        )}
      </Modal>
    </div>
  );
}
